import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDraggable, useDroppable } from "@dnd-kit/core"
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  PlayIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { type Filter, type Query } from "@cubejs-client/core"
import { assetToScrapedImage, ImageCategory, type ScrapedImage } from "../types"
import { aspectRatioLabel } from "./aspectRatio"
import { downloadBlob } from "./overlay/export-overlay"
import { useAdForgeProxyImageMutation } from "~/ui-rtk/api/adForgeApi"
import { toast } from "react-toastify"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  MetricsMapping,
  type PostProcessResultSync,
} from "~/ui-rtk/constants/metrics-mapping"
import {
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"
import useDate from "~/ui-rtk/hooks/date"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import {
  assetsApi,
  useBulkProductTagsQuery,
  useListAssetsQuery,
  type Asset,
  type AssetProductTag,
} from "~/ui-rtk/api/assetsApi"
import ProductTagPopover from "./ProductTagPopover"
import {
  cacheAssetInfo,
  type CachedAssetInfo,
} from "~/ui-rtk/app/slices/asset-cache.slice"
import type { AssetSourceType } from "~/ui-rtk/components/atoms/MarathonImage/MarathonImage"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import DateRangePicker from "~/ui-rtk/components/ui/controls/DateRangePicker/DateRangePicker"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { formatNumber } from "~/ui-rtk/utils/format"
import { motion, type Variants } from "motion/react"

const dayjs = useDate()

export const DRAWER_DROPPABLE_ID = "drawer:pool"
export const DRAWER_ITEM_PREFIX = "drawer:"

// Drawer animations run IN SYNC with the width expansion (no delay). Same
// duration + ease as the aside's `transition-[width] duration-200 ease-out`
// so the sections appear to be carried in by the drawer itself rather than
// trailing it. Direction is from the right to match the panel's slide.
const itemsGridVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.02, delayChildren: 0 },
  },
}
const itemTileVariants: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
}

const drawerSectionsVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.03, delayChildren: 0 },
  },
}
const drawerSectionVariants: Variants = {
  hidden: { opacity: 0, x: 32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
}

const TABS = [
  { key: "selected", label: "Selected" },
  { key: "latest", label: "Latest" },
  { key: "top-ads", label: "Top Ads" },
  { key: "top-posts", label: "Top Posts" },
  { key: "top-products", label: "Top Products" },
] as const

// Source filter for the Latest tab — a sixth tab didn't fit the drawer's
// strip, so "only my uploads" lives as a sub-filter instead.
const LIBRARY_SOURCES = [
  { key: "all", label: "All" },
  { key: "uploads", label: "Uploads" },
] as const
type LibrarySource = (typeof LIBRARY_SOURCES)[number]["key"]

const LIBRARY_PAGE_SIZE = 100
type TabKey = (typeof TABS)[number]["key"]

// ── Converters: turn each tab's native row into a ScrapedImage ────────────

// Cube-row shape (after postProcess strips the "company_media_metrics_ads."
// prefix). We only consume the fields we render — the cube response contains
// many more columns we ignore.
type AdRow = {
  ad_id?: string
  media_channel?: string
  ad_name?: string
  md_image_url?: string
}
type PostRow = {
  [k: string]: string | number | undefined
}
// Cube row from `company_web_analytics_page_path_report` — keys keep the
// prefix because we don't run postProcess on this query.
type ProductRow = {
  [k: string]: string | number | undefined
}

// Resolved URLs come ONLY from the asset-cache slice (populated by the
// resolve effect below) — no raw cube URL fallback so cards never flash
// broken thumbnails. Rows whose resolve hasn't landed are simply hidden.
// `assetId` is stamped from the cached asset info so AI flows (extend
// placements, motion video, image variations) can hand the backend a
// library id instead of base64.
function adRowToScrapedImage(
  row: AdRow,
  cachedAssets: Record<string, CachedAssetInfo>,
): ScrapedImage | null {
  if (!row.ad_id || !row.media_channel) return null
  const sourceType = mapMediaChannelToSourceType(String(row.media_channel))
  const cacheKey = `${sourceType}:${row.ad_id}`
  const info = cachedAssets[cacheKey]
  if (!info?.url) return null
  return {
    id: `ad-${row.ad_id}`,
    url: info.url,
    thumbnailUrl: info.thumbnailUrl ?? info.url,
    type: info.mediaType === "VIDEO" ? "video" : "image",
    category: ImageCategory.OTHER,
    color: "Default",
    selected: true,
    sourceUrl: `Top Ad - ${row.media_channel ?? ""}`.trim(),
    assetId: info.assetId,
    width: info.width ?? undefined,
    height: info.height ?? undefined,
    ...(info.variantCount && { variantCount: info.variantCount }),
  }
}

function postRowToScrapedImage(
  row: PostRow,
  prefix: string,
  cachedAssets: Record<string, CachedAssetInfo>,
): ScrapedImage | null {
  const postId = row[`${prefix}.post_id`] as string | undefined
  const channel = row[`${prefix}.media_channel`] as string | undefined
  if (!postId || !channel) return null
  const sourceType = mapMediaChannelToSourceType(String(channel), true)
  const cacheKey = `${sourceType}:${postId}`
  const info = cachedAssets[cacheKey]
  if (!info?.url) return null
  return {
    id: `post-${postId}`,
    url: info.url,
    thumbnailUrl: info.thumbnailUrl ?? info.url,
    type: info.mediaType === "VIDEO" ? "video" : "image",
    category: ImageCategory.OTHER,
    color: "Default",
    selected: true,
    sourceUrl: `Organic - ${channel ?? ""}`.trim(),
    assetId: info.assetId,
    width: info.width ?? undefined,
    height: info.height ?? undefined,
    ...(info.variantCount && { variantCount: info.variantCount }),
  }
}

function productRowToScrapedImage(
  row: ProductRow,
  prefix: string,
  cachedAssets: Record<string, CachedAssetInfo>,
): ScrapedImage | null {
  const id = row[`${prefix}.id`] as string | undefined
  if (!id) return null
  const info = cachedAssets[`SHOPIFY_PRODUCT:${id}`]
  if (!info?.url) return null
  const title = row[`${prefix}.title`] as string | undefined
  return {
    id: `product-${id}`,
    url: info.url,
    thumbnailUrl: info.thumbnailUrl ?? info.url,
    type: info.mediaType === "VIDEO" ? "video" : "image",
    category: ImageCategory.OTHER,
    color: "Default",
    selected: true,
    sourceUrl: title ? `Shopify - ${title}` : "Shopify Product",
    assetId: info.assetId,
    width: info.width ?? undefined,
    height: info.height ?? undefined,
    ...(info.variantCount && { variantCount: info.variantCount }),
  }
}

/**
 * Decode a `data:` URL into a Blob WITHOUT `fetch()`. The prod CSP/proxy
 * allow-list blocks client-side `fetch('data:…')`, which is exactly how the
 * earlier AdForge image flows broke — decode the base64 by hand instead.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, payload] = dataUrl.split(",")
  const mime = /data:([^;]+)/.exec(header)?.[1] ?? "application/octet-stream"
  const binary = atob(payload)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** Filename for a downloaded asset: its library id plus the real extension. */
function downloadFilenameFor(asset: ScrapedImage, blob: Blob): string {
  const fromUrl = /\.([a-z0-9]{3,4})(?:$|[?#])/i.exec(asset.url ?? "")?.[1]
  const fromMime = blob.type.split("/")[1]?.split("+")[0]
  const extension =
    fromUrl || fromMime || (asset.type === "video" ? "mp4" : "jpg")
  return `${asset.assetId ?? asset.id}.${extension}`
}

// ── Item card (draggable) ──────────────────────────────────────────────────

interface DrawerItemProps {
  asset: ScrapedImage
  tab: TabKey
  slotIndices: number[]
  isDemoEnabled: boolean
  /** True when this asset's id is in the picked-assets pool — shows a
   *  "Selected" badge on catalog tabs so the user can see what they've
   *  already staged without leaving the tab. */
  isPicked?: boolean
  /** Placement slot labels this asset is used as a per-placement variant for
   *  (e.g. ["Stories"]). Marks it as in-use the same way a card slot does —
   *  without this, a draft's saved placement variants render as unselected
   *  strangers when the creative is re-opened for editing. */
  placementLabels?: string[]
  /** Formatted sort-metric value for this asset (e.g. "$1.2K" for Brand
   *  Value, "230" for Shares). Rendered as a small badge so users see the
   *  actual ranking value at a glance. Omitted on the Selected tab and on
   *  any row whose metric isn't available. */
  metricValue?: string
  onRemoveFromPool?: (id: string) => void
  /** Tap-to-stage: when set, a click (not drag) toggles the asset in the
   *  Selected pool. Wired only for the catalog tabs (Top Ads / Posts /
   *  Products) — Selected items are already staged so it'd be a no-op. */
  onTap?: (asset: ScrapedImage) => void
  /** Open the thumbnail-frame picker. Rendered for library videos only —
   *  the pick persists via POST /assets/:id/thumbnail, so a synthetic pool
   *  id (blob fallback, mega-feed seed) has nowhere to store it. */
  onPickThumbnail?: (asset: ScrapedImage) => void
  /** Save the original file to disk. */
  onDownload?: (asset: ScrapedImage) => void
  /** This item's download is in flight (fetch + possible proxy round-trip). */
  isDownloading?: boolean
  /** Fired when the thumbnail fails to load. Drawer filters the failed
   *  asset out of the visible list. */
  onImageError?: (assetId: string) => void
  /** Feed products this asset is tagged into (any source). Non-empty means
   *  "in feed" — the badge + the tag popover's chip list feed off it. */
  productTags?: AssetProductTag[]
}

function DrawerItem({
  asset,
  tab,
  slotIndices,
  isDemoEnabled,
  isPicked,
  placementLabels,
  metricValue,
  onRemoveFromPool,
  onTap,
  onPickThumbnail,
  onDownload,
  isDownloading,
  onImageError,
  productTags,
}: DrawerItemProps) {
  // ID is namespaced by tab + asset id so it never collides with slot
  // draggables, and so the same asset can render in multiple tabs without
  // tripping dnd-kit's "duplicate id" warning.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${DRAWER_ITEM_PREFIX}${tab}:${asset.id}`,
    // Carry the full ScrapedImage so the page-level drag-end handler can
    // place items from tabs that aren't yet in `pickedAssets` (e.g. Top Ads).
    data: { kind: "pool", asset },
  })

  const inSlot = slotIndices.length > 0
  const inPlacement = !!placementLabels?.length
  // An original that already has AI children (enhance / extend / motion /
  // compose). A variant itself normally has none, so this reads as "this is
  // the source".
  const variantCount = asset.variantCount ?? 0
  const hasVariants = variantCount > 0
  const thumb = asset.thumbnailUrl || asset.url
  // Assets synced before the upload path started recording dimensions (most
  // notably every AI-generated variant) have none on the row. Fall back to
  // what the browser decoded — our thumbnails scale uniformly, so the ratio
  // this yields matches the original even though the pixel count doesn't.
  const [decodedDims, setDecodedDims] = useState<{
    width: number
    height: number
  } | null>(null)
  const ratio = aspectRatioLabel(
    asset.width ?? decodedDims?.width,
    asset.height ?? decodedDims?.height,
  )
  const usageBadge = (() => {
    if (inSlot) {
      if (slotIndices.length === 1) return `Card ${slotIndices[0] + 1}`
      return `×${slotIndices.length}`
    }
    // Placement variants aren't cards, but they ARE part of the ad — name the
    // slot(s) they fill so the user can tell why this one is highlighted.
    if (inPlacement) {
      const [first, ...rest] = placementLabels as string[]
      return rest.length ? `${first} +${rest.length}` : first
    }
    return null
  })()
  const tooltipText = (() => {
    // The badges live under a pointer-events-none layer (so they never steal
    // the drag), which means their own `title` can't fire — the variant count
    // and the tagged feed products ride on the card's tooltip instead.
    const variantNote = hasVariants
      ? ` · original — ${variantCount} AI variant${variantCount === 1 ? "" : "s"} made from this`
      : ""
    const feedNote = productTags?.length
      ? ` · in feed: ${productTags
          .map(t => t.productName ?? t.productRefId)
          .join(", ")}`
      : ""
    const notes = variantNote + feedNote
    if (onTap) {
      return (
        (isPicked
          ? "Tap to remove from Selected, or drag into a card slot"
          : "Tap to stage in Selected, or drag into a card slot") + notes
      )
    }
    if (inSlot) {
      return `Used in card ${slotIndices.map(i => i + 1).join(", ")} — drag for another copy${notes}`
    }
    if (inPlacement) {
      return `Used as the ${(placementLabels as string[]).join(", ")} placement${notes}`
    }
    return `Drag into a card slot${notes}`
  })()
  // Video previews never go through the image error filter: images that
  // error are dropped from the grid entirely, but a video tile must stay
  // rendered (it's draggable and stageable) whatever its preview does —
  // poster fails → try the <video>; that fails too → placeholder.
  const [videoPreviewFailed, setVideoPreviewFailed] = useState(false)
  const [videoPosterFailed, setVideoPosterFailed] = useState(false)
  const mediaContent = (() => {
    if (asset.type === "video") {
      // Several converters (draft hydration, catalog rows, blob fallbacks)
      // backfill `thumbnailUrl` with the video URL itself when no real
      // thumbnail exists. An <img> pointing at a video file always errors,
      // and via the image error path that silently deleted the tile from
      // the grid. Treat such a thumbnail as absent.
      const posterUrl =
        asset.thumbnailUrl && asset.thumbnailUrl !== asset.url
          ? asset.thumbnailUrl
          : undefined
      if (posterUrl && !videoPosterFailed) {
        return (
          <img
            src={posterUrl}
            alt=""
            className="w-full h-full object-cover pointer-events-none"
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={
              asset.width && asset.height
                ? undefined
                : e =>
                    setDecodedDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
            }
            onError={() => setVideoPosterFailed(true)}
          />
        )
      }
      if (!asset.url || videoPreviewFailed) {
        return (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-system-gray-500">
            no preview
          </div>
        )
      }
      return (
        <video
          // `#t=0.1` seeks just past frame zero so the browser actually
          // paints a preview frame — Safari (and Chrome in some cases)
          // otherwise leaves the element blank, which reads as the card
          // not rendering at all.
          src={`${asset.url}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          onError={() => setVideoPreviewFailed(true)}
          onLoadedMetadata={
            asset.width && asset.height
              ? undefined
              : e =>
                  setDecodedDims({
                    width: e.currentTarget.videoWidth,
                    height: e.currentTarget.videoHeight,
                  })
          }
          className="w-full h-full object-cover pointer-events-none"
        />
      )
    }
    if (thumb) {
      return (
        <img
          src={thumb}
          alt=""
          className="w-full h-full object-cover pointer-events-none"
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={
            asset.width && asset.height
              ? undefined
              : e =>
                  setDecodedDims({
                    width: e.currentTarget.naturalWidth,
                    height: e.currentTarget.naturalHeight,
                  })
          }
          onError={onImageError ? () => onImageError(asset.id) : undefined}
        />
      )
    }
    return (
      <div className="w-full h-full flex items-center justify-center text-[10px] text-system-gray-500">
        no preview
      </div>
    )
  })()

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onTap ? () => onTap(asset) : undefined}
      role={onTap ? "button" : undefined}
      className={cn(
        "group relative aspect-square rounded-lg overflow-hidden ring-1 transition select-none touch-none cursor-grab active:cursor-grabbing",
        inSlot || isPicked || inPlacement
          ? "ring-2 ring-brand"
          : "ring-system-gray-700/50 hover:ring-brand/40",
        isDragging && "opacity-30",
      )}
      title={tooltipText}
    >
      <div
        className={cn(
          "w-full h-full bg-system-gray-900",
          isDemoEnabled && "blur-md",
        )}
      >
        {mediaContent}
      </div>

      {asset.type === "video" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-7 h-7 rounded-full bg-black/55 flex items-center justify-center">
            <PlayIcon className="w-3.5 h-3.5 text-white/85" />
          </div>
        </div>
      )}

      {/* Top-left badge stack: how the asset is used in the ad, then whether
          it's an original that already has AI variants. Stacked (not
          alternating) so an in-use original shows both. */}
      <div className="absolute top-1 left-1 flex flex-col items-start gap-0.5 pointer-events-none">
        {usageBadge && (
          <div className="bg-brand/85 rounded px-1 py-0.5">
            <span className="text-[9px] text-white font-bold">
              {usageBadge}
            </span>
          </div>
        )}
        {/* "Selected" cue for catalog items already staged in the pool.
            Suppressed when a usage badge is present — that already says the
            asset is in play, and says it more precisely. */}
        {isPicked && !usageBadge && (
          <div className="bg-brand rounded px-1 py-0.5 flex items-center gap-0.5 shadow">
            <CheckIcon className="w-2.5 h-2.5 text-white" />
            <span className="text-[9px] text-white font-bold">Selected</span>
          </div>
        )}
        {/* Original with AI children — without this the source and the
            variants generated from it read as unrelated tiles, and users
            re-generate work they already have. The count rides on the card's
            tooltip; the tile itself just names what it is. */}
        {hasVariants && (
          <div className="bg-black/75 rounded px-1 py-0.5 flex items-center gap-0.5">
            <SparklesIcon className="w-2.5 h-2.5 text-brand" />
            <span className="text-[9px] text-white font-bold">
              Original{variantCount > 1 ? ` ×${variantCount}` : ""}
            </span>
          </div>
        )}
        {/* Tagged into ≥1 product's feed pool (any source). Which products
            it's in rides on the card's tooltip — badges can't carry their
            own `title` under the pointer-events-none layer. */}
        {!!productTags?.length && (
          <div className="bg-brand/85 rounded px-1 py-0.5">
            <span className="text-[9px] text-white font-bold">In feed</span>
          </div>
        )}
      </div>

      {/* Bottom-left metric badge — only on catalog tabs. Shows the actual
          sort-metric value (e.g. "$1.2K" Brand Value) so users can compare
          assets at a glance. */}
      {metricValue && (
        <div className="absolute bottom-1 left-1 bg-black/70 rounded px-1 py-0.5 pointer-events-none">
          <span className="text-[9px] text-white font-bold">{metricValue}</span>
        </div>
      )}

      {/* Bottom-right media-type badge — tiny icon so users can tell videos
          and images apart even before the thumbnail renders. The asset's
          aspect ratio rides along when its dimensions are known, so users can
          match an asset to a placement slot without opening it. */}
      <div
        className="absolute bottom-1 right-1 bg-black/70 rounded px-0.5 py-0.5 pointer-events-none flex items-center justify-center gap-0.5"
        title={
          asset.width && asset.height
            ? `${asset.width}×${asset.height}px`
            : undefined
        }
      >
        {asset.type === "video" ? (
          <PlayIcon className="w-2.5 h-2.5 text-white" />
        ) : (
          <PhotoIcon className="w-2.5 h-2.5 text-white" />
        )}
        {ratio && (
          <span className="text-[9px] leading-none text-white font-bold">
            {ratio}
          </span>
        )}
      </div>

      {/* Pick a thumbnail frame — every video gets it, sitting left of the
          feed popover. Thumbnail-less videos especially need it: the chosen
          frame becomes the tile preview here AND what Meta shows before the
          video plays. Videos without a library id are resolved by URL in
          the click handler (which explains itself when it can't). */}
      {onPickThumbnail && asset.type === "video" && (
        <button
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            onPickThumbnail(asset)
          }}
          title="Pick a thumbnail frame from this video"
          aria-label="Pick thumbnail frame"
          className={cn(
            "absolute top-1 w-5 h-5 rounded-full flex items-center justify-center bg-black/70 hover:bg-brand text-white transition opacity-0 group-hover:opacity-100",
            // Hover row stacks right-to-left: remove (Selected tab only),
            // download (always), feed popover (library assets only), then
            // this — shift left one 1.5rem slot per button present.
            ["right-7", "right-[3.25rem]", "right-[4.75rem]"][
              (onRemoveFromPool ? 1 : 0) + (asset.assetId ? 1 : 0)
            ],
          )}
        >
          <PhotoIcon className="w-3 h-3" />
        </button>
      )}

      {/* Save-to-feed popover — sits left of Download. Only library assets
          can carry product tags, so synthetic pool ids (blob fallbacks,
          mega-feed seeds) don't get the button. The wrapper swallows
          pointerdown/click exactly like the buttons below so dnd-kit never
          reads the tap as a drag, and `focus-within` keeps an OPEN popover
          visible after the pointer leaves the card. */}
      {asset.assetId && (
        <div
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className={cn(
            "absolute top-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition",
            onRemoveFromPool ? "right-[3.25rem]" : "right-7",
          )}
        >
          <ProductTagPopover
            assetId={asset.assetId}
            productTags={productTags ?? []}
          />
        </div>
      )}

      {/* Save the original file. Sits left of the remove button when that one
          is present (Selected tab) and alone in the corner otherwise, so it's
          available on the catalog tabs too. Stays visible while in flight —
          the proxy fallback can take a moment on a large image. */}
      {onDownload && (
        <button
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            onDownload(asset)
          }}
          disabled={isDownloading}
          title="Download the original file"
          aria-label="Download the original file"
          className={cn(
            "absolute top-1 w-5 h-5 rounded-full flex items-center justify-center bg-black/70 hover:bg-brand text-white transition disabled:opacity-70",
            onRemoveFromPool ? "right-7" : "right-1",
            isDownloading ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          )}
        >
          {isDownloading ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <ArrowDownTrayIcon className="w-3 h-3" />
          )}
        </button>
      )}

      {onRemoveFromPool && (
        <button
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            onRemoveFromPool(asset.id)
          }}
          title="Remove from your assets"
          aria-label="Remove from your assets"
          className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center bg-black/70 hover:bg-red-500 text-white transition opacity-0 group-hover:opacity-100"
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}

// ── Loading / empty placeholders ──────────────────────────────────────────

function TabSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-system-gray-500">
      <span className="loading loading-spinner loading-sm text-brand mb-2" />
      <p className="text-[11px]">{label}</p>
    </div>
  )
}

function TabEmpty({
  icon: Icon,
  message,
  hint,
}: {
  icon: typeof PhotoIcon
  message: string
  hint?: string
}) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center text-system-gray-500 px-4">
      <Icon className="w-9 h-9 mb-2 opacity-40" />
      <p className="text-xs">{message}</p>
      {hint && <p className="text-[10px] mt-1 opacity-70">{hint}</p>}
    </div>
  )
}

// ── Main drawer ───────────────────────────────────────────────────────────

interface SelectedAssetsDrawerProps {
  pickedAssets: ScrapedImage[]
  adAssetIds: string[]
  /** Asset id → placement slot labels it fills on some card (e.g. "Stories").
   *  Keyed by both the local card id and the library `assetId` so a variant
   *  is recognised whichever list it shows up in. */
  placementLabelsByAssetId?: Map<string, string[]>
  isDemoEnabled: boolean
  isUploading: boolean
  onRemoveFromPool: (id: string) => void
  /** Called when a user taps an item in a catalog tab — parent should stage
   *  it into the picked-assets pool. The drawer then switches the active tab
   *  to "Selected" so the result is visible. */
  onAddToPool: (asset: ScrapedImage) => void
  onUploadClick: () => void
  onScrapeClick: () => void
  /** Open the video thumbnail picker for a library video — available on
   *  every video tile so a frame can be set before the video ever reaches a
   *  card slot (thumbnail-less videos are exactly the ones that need it). */
  onPickThumbnail?: (asset: ScrapedImage) => void
}

// Default date range for the catalog tabs — last 6 months back to today.
const DEFAULT_RANGE_DAYS = 180

// Per-tab sort options. Values are the cube measure used in `order`.
const AD_SORTS = [
  { value: "brand_value_agg", label: "Brand Value" },
  { value: "purchase_value", label: "Purchase Value" },
] as const
const POST_SORTS = [
  { value: "brand_value_agg", label: "Brand Value" },
  { value: "shares", label: "Shares" },
  { value: "saves", label: "Saves" },
] as const
const PRODUCT_SORTS = [
  { value: "sum_first_order_revs", label: "First Order Revenue" },
  { value: "total_revenue", label: "Total Revenue" },
] as const

export default function SelectedAssetsDrawer({
  pickedAssets,
  adAssetIds,
  placementLabelsByAssetId,
  isDemoEnabled,
  isUploading,
  onRemoveFromPool,
  onAddToPool,
  onUploadClick,
  onScrapeClick,
  onPickThumbnail,
}: SelectedAssetsDrawerProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: DRAWER_DROPPABLE_ID,
    data: { kind: "pool" },
  })

  const [activeTab, setActiveTab] = useState<TabKey>("selected")
  // `search` is the in-flight input value; `debouncedSearch` is what the
  // cube queries actually use. We only commit `search → debouncedSearch` on
  // blur or Enter, not on every keystroke — typing doesn't fire requests.
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const commitSearch = useCallback(() => {
    setDebouncedSearch(prev => {
      const next = search.trim()
      return prev === next ? prev : next
    })
  }, [search])
  const clearSearch = useCallback(() => {
    setSearch("")
    setDebouncedSearch("")
  }, [])

  // Shared date range applied to every catalog tab's cube query. Initialised
  // to a 6-month trailing window; the picker lets the user adjust it.
  const [dateRangeStart, setDateRangeStart] = useState<Date>(() =>
    dayjs().subtract(DEFAULT_RANGE_DAYS, "day").startOf("day").toDate(),
  )
  const [dateRangeEnd, setDateRangeEnd] = useState<Date>(() =>
    dayjs().endOf("day").toDate(),
  )
  // Per-tab sort selection — each tab's first option is the default.
  const [adsSortBy, setAdsSortBy] =
    useState<(typeof AD_SORTS)[number]["value"]>("brand_value_agg")
  const [postsSortBy, setPostsSortBy] =
    useState<(typeof POST_SORTS)[number]["value"]>("brand_value_agg")
  const [productsSortBy, setProductsSortBy] = useState<
    (typeof PRODUCT_SORTS)[number]["value"]
  >("sum_first_order_revs")

  // "Latest" tab — newest synced assets in the company library, with a
  // source sub-filter (All / Uploads-only). Pages GET /assets; backend
  // orders by `createdAt` desc, so each page returns the next-oldest batch.
  // We accumulate pages in a Map keyed by asset id (dedupes if the same row
  // gets re-emitted) so "Show more" extends the visible list instead of
  // replacing it.
  const isLibraryTab = activeTab === "latest"
  const [librarySource, setLibrarySource] = useState<LibrarySource>("all")
  const [libraryOffset, setLibraryOffset] = useState(0)
  const [libraryMap, setLibraryMap] = useState<Map<string, Asset>>(new Map())
  const [libraryHasMore, setLibraryHasMore] = useState(true)
  // Reset accumulated pages on tab or source-filter change — a stale page
  // from the other filter must not linger. On re-entry the user gets a
  // fresh top of the list (catches any new uploads).
  useEffect(() => {
    setLibraryOffset(0)
    setLibraryMap(new Map())
    setLibraryHasMore(true)
  }, [activeTab, librarySource])
  const { data: libraryAssetsData, isFetching: isLoadingLibrary } =
    useListAssetsQuery(
      {
        limit: LIBRARY_PAGE_SIZE,
        offset: libraryOffset,
        status: "ACTIVE",
        syncStatus: "SYNCED",
        ...(librarySource === "uploads"
          ? { sourceType: "UPLOAD" as const }
          : {}),
      },
      { skip: !isLibraryTab, refetchOnMountOrArgChange: true },
    )
  useEffect(() => {
    if (!libraryAssetsData) return
    const page = libraryAssetsData.assets
    setLibraryMap(prev => {
      const next =
        libraryOffset === 0 ? new Map<string, Asset>() : new Map(prev)
      for (const a of page) next.set(a.id, a)
      return next
    })
    // The endpoint returns the exact match count, so gate Load More on it
    // instead of the page-length heuristic — a full last page no longer
    // leaves a dangling button that fetches an empty page.
    setLibraryHasMore(libraryOffset + page.length < libraryAssetsData.total)
  }, [libraryAssetsData, libraryOffset])
  const libraryItems = useMemo<ScrapedImage[]>(
    () => Array.from(libraryMap.values()).map(assetToScrapedImage),
    [libraryMap],
  )
  const loadMoreLibrary = useCallback(() => {
    if (isLoadingLibrary || !libraryHasMore) return
    setLibraryOffset(prev => prev + LIBRARY_PAGE_SIZE)
  }, [isLoadingLibrary, libraryHasMore])

  // Asset ids that failed to load (404, CORS, etc.) — drop them from the
  // visible list. Tracked by ScrapedImage.id so failures in one tab don't
  // hide the same underlying asset in another (catalog tabs use distinct
  // id prefixes: `ad-`, `post-`, `product-`).
  const [failedAssetIds, setFailedAssetIds] = useState<Set<string>>(new Set())
  const markAssetFailed = useCallback((id: string) => {
    setFailedAssetIds(prev => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])
  // Clear the failed set whenever the user moves to a different surface
  // (tab switch, date-range change, or starts/clears a search). Without
  // this, a transient CDN/network blip during initial load would hide
  // those rows permanently for the rest of the session — the user would
  // need to reload the whole page. Resetting here lets new fetches give
  // the failed assets another chance.
  useEffect(() => {
    setFailedAssetIds(prev => (prev.size === 0 ? prev : new Set()))
  }, [activeTab, dateRangeStart, dateRangeEnd, debouncedSearch])

  // Map of asset id → slot indices it occupies, so each item can show how
  // many cards it's used in.
  const slotsByAssetId = useMemo(() => {
    const map = new Map<string, number[]>()
    adAssetIds.forEach((id, idx) => {
      const list = map.get(id) ?? []
      list.push(idx)
      map.set(id, list)
    })
    return map
  }, [adAssetIds])

  // Lookup for "is this catalog item already in the pool?". Used to mark
  // selected thumbnails with the Selected badge.
  const pickedAssetIds = useMemo(
    () => new Set(pickedAssets.map(a => a.id)),
    [pickedAssets],
  )

  // ── Download the original file ────────────────────────────────────────
  // Straight `fetch` first — the S3 bucket serves the asset directly when CORS
  // allows it, and that keeps the bytes off our API. Cross-origin responses
  // that block the read fall back to the server-side image proxy. (An
  // `<a download>` pointing at the CDN is not an option: browsers ignore the
  // `download` attribute cross-origin and just navigate to the image.)
  const [proxyImage] = useAdForgeProxyImageMutation()
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const handleDownload = useCallback(
    async (asset: ScrapedImage) => {
      const source = asset.url || asset.thumbnailUrl
      if (!source) {
        toast.error("This asset has no downloadable file.")
        return
      }
      setDownloadingId(asset.id)
      try {
        let blob: Blob | null = null
        try {
          const res = await fetch(source)
          if (res.ok) blob = await res.blob()
        } catch {
          // CORS or network — the proxy below is the fallback.
        }
        if (!blob) {
          if (asset.type === "video") {
            // The image proxy can't serve video bytes, and re-hosting them
            // isn't something this button should do. Say so plainly.
            throw new Error(
              "This video can't be downloaded from here — open it in a new tab and save it.",
            )
          }
          const { base64 } = await proxyImage({ url: source }).unwrap()
          blob = dataUrlToBlob(base64)
        }
        downloadBlob(blob, downloadFilenameFor(asset, blob))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Download failed")
      } finally {
        setDownloadingId(null)
      }
    },
    [proxyImage],
  )

  // ── Top Ads — paged cube call using the production
  // `brand_media_optimization_ads_table` mapping. All three "Top" tabs use
  // the same page size and the same scroll-to-load-more pattern so the UX
  // is consistent. 20 sits comfortably under the assets-resolve cap (100 per
  // request) so each page's `resolveRowAssets` pass fits in one shot.
  const AD_PREFIX = "company_media_metrics_ads"
  const TOP_PAGE_SIZE = 20

  const [topAdsOffset, setTopAdsOffset] = useState(0)
  const [topAdsMap, setTopAdsMap] = useState<Map<string, AdRow>>(new Map())
  const [topAdsHasMore, setTopAdsHasMore] = useState(true)
  // Reset paging when filters change so the next request starts from page 0.
  // The accumulate effect below replaces the map atomically when the fresh
  // page-0 response lands (it builds a new Map whenever offset === 0), so we
  // don't clear `topAdsMap` here — clearing causes images to flash out.
  useEffect(() => {
    setTopAdsOffset(0)
    setTopAdsHasMore(true)
  }, [dateRangeStart, dateRangeEnd, adsSortBy])
  const topAdsQuery = useMemo<Query>(() => {
    const { q } = MetricsMapping.brand_media_optimization_ads_table
    const dateStart = dayjs(dateRangeStart).startOf("day").toDate()
    const today = dayjs(dateRangeEnd).endOf("day").toDate()
    return polyfillCubeQueryWithTimeDimension(
      {
        ...(q as Partial<Query>),
        order: [[`${AD_PREFIX}.${adsSortBy}`, "desc"]],
        limit: TOP_PAGE_SIZE,
        offset: topAdsOffset,
      } as Query,
      { dateRange: [dateStart, today] },
    )
  }, [topAdsOffset, dateRangeStart, dateRangeEnd, adsSortBy])
  const { resultSet: topAdsResultSet, isLoading: isLoadingAds } =
    useCubeQuery(topAdsQuery)
  const topAdsPage = useMemo<AdRow[]>(() => {
    if (!topAdsResultSet) return []
    const { postProcess } = MetricsMapping.brand_media_optimization_ads_table
    const pivot = postProcess?.(
      getPivotData(topAdsResultSet, topAdsQuery),
      {},
    ) as PostProcessResultSync | undefined
    return (pivot?.[0] ?? []) as AdRow[]
  }, [topAdsResultSet, topAdsQuery])

  // ── Top Posts — same paging pattern.
  const POST_PREFIX = "company_media_metrics_organic_posts"
  const [topPostsOffset, setTopPostsOffset] = useState(0)
  const [topPostsMap, setTopPostsMap] = useState<Map<string, PostRow>>(
    new Map(),
  )
  const [topPostsHasMore, setTopPostsHasMore] = useState(true)
  const lastTopPostsOffsetRef = useRef<number | null>(null)
  useEffect(() => {
    setTopPostsOffset(0)
    setTopPostsHasMore(true)
    lastTopPostsOffsetRef.current = null
  }, [dateRangeStart, dateRangeEnd, postsSortBy])
  const topPostsQuery = useMemo<Query>(() => {
    const { q } = MetricsMapping.brand_media_organic_social_posts_table
    const dateStart = dayjs(dateRangeStart).startOf("day").toDate()
    const today = dayjs(dateRangeEnd).endOf("day").toDate()
    return polyfillCubeQueryWithTimeDimension(
      {
        ...(q as Partial<Query>),
        filters: [
          {
            dimension: `${POST_PREFIX}.${postsSortBy}`,
            operator: "gt",
            values: ["0"],
          },
        ] as Filter[],
        order: [[`${POST_PREFIX}.${postsSortBy}`, "desc"]],
        limit: TOP_PAGE_SIZE,
        offset: topPostsOffset,
      } as Query,
      { dateRange: [dateStart, today] },
    )
  }, [topPostsOffset, dateRangeStart, dateRangeEnd, postsSortBy])
  const { resultSet: topPostsResultSet, isLoading: isLoadingPosts } =
    useCubeQuery(topPostsQuery)
  const topPostsPage = useMemo<PostRow[]>(() => {
    if (!topPostsResultSet) return []
    const { postProcess } =
      MetricsMapping.brand_media_organic_social_posts_table
    const pivot = postProcess?.(
      getPivotData(topPostsResultSet, topPostsQuery),
      {},
    ) as PostProcessResultSync | undefined
    return (pivot?.[0] ?? []) as PostRow[]
  }, [topPostsResultSet, topPostsQuery])

  // ── Accumulate each page into a Map (keyed by ad_id / post_id) so prior
  // pages stay rendered while the next loads. `hasMore` flips false when a
  // page returns fewer rows than asked. We gate on resultSet identity so
  // transient renders — where the offset has already changed but cubejs
  // hasn't returned the new resultSet yet — don't run accumulate against
  // stale data and freeze hasMore at the wrong value.
  const lastProcessedAdsResultRef = useRef<unknown>(null)
  useEffect(() => {
    if (isLoadingAds || !topAdsResultSet) return
    if (lastProcessedAdsResultRef.current === topAdsResultSet) return
    lastProcessedAdsResultRef.current = topAdsResultSet
    setTopAdsMap(prev => {
      const next = topAdsOffset === 0 ? new Map<string, AdRow>() : new Map(prev)
      for (const row of topAdsPage) {
        if (row.ad_id) next.set(row.ad_id, row)
      }
      return next
    })
    setTopAdsHasMore(topAdsPage.length >= TOP_PAGE_SIZE)
  }, [topAdsResultSet, topAdsPage, topAdsOffset, isLoadingAds])

  const lastProcessedPostsResultRef = useRef<unknown>(null)
  useEffect(() => {
    if (isLoadingPosts || !topPostsResultSet) return
    if (lastProcessedPostsResultRef.current === topPostsResultSet) return
    lastProcessedPostsResultRef.current = topPostsResultSet
    setTopPostsMap(prev => {
      const next =
        topPostsOffset === 0 ? new Map<string, PostRow>() : new Map(prev)
      for (const row of topPostsPage) {
        const postId = row[`${POST_PREFIX}.post_id`] as string | undefined
        if (postId) next.set(postId, row)
      }
      return next
    })
    setTopPostsHasMore(topPostsPage.length >= TOP_PAGE_SIZE)
  }, [topPostsResultSet, topPostsPage, topPostsOffset, isLoadingPosts])

  // Materialize the accumulated rows for downstream rendering.
  const topAds = useMemo(() => Array.from(topAdsMap.values()), [topAdsMap])
  const topPosts = useMemo(
    () => Array.from(topPostsMap.values()),
    [topPostsMap],
  )

  // ── Asset resolution — a SINGLE consolidated request across all visible
  // sources (ads, posts, products + their search counterparts). The three
  // cube queries return at different times and used to fire three separate
  // resolve requests in the same tick, which spiked the resolve endpoint and
  // caused errors. We now coalesce on a short debounce: any source-row
  // change resets the timer, and once things settle we collect every
  // not-yet-cached asset key and fire one request. A signature ref prevents
  // re-firing the same payload (e.g. when the cache update re-renders).
  const dispatch = useAppDispatch()
  // Full asset info (URL + library id + dims). Converters use this — not the
  // URL-only `urls` map — so AI flows can hand the backend an asset id.
  const cachedAssetUrls = useAppSelector(state => state.assetCacheSlice.urls)
  const cachedAssets = useAppSelector(state => state.assetCacheSlice.assets)
  const lastResolvedSigRef = useRef<string>("")
  const [isResolving, setIsResolving] = useState(false)

  const loadMoreTopAds = useCallback(() => {
    if (isLoadingAds || !topAdsHasMore) return
    setTopAdsOffset(prev => prev + TOP_PAGE_SIZE)
  }, [isLoadingAds, topAdsHasMore])
  const loadMoreTopPosts = useCallback(() => {
    if (isLoadingPosts || !topPostsHasMore) return
    setTopPostsOffset(prev => prev + TOP_PAGE_SIZE)
  }, [isLoadingPosts, topPostsHasMore])

  // ── Top Products — paged cube call on `company_web_analytics_page_path_report`,
  //    matching the pattern of Top Ads / Top Posts. Thumbnails come from the
  //    asset-resolve endpoint via SHOPIFY_PRODUCT, so we don't fetch the
  //    Shopify CDN URL from the cube anymore.
  const PRODUCT_PREFIX = "company_web_analytics_page_path_report"
  const [productsOffset, setProductsOffset] = useState(0)
  const [productsMap, setProductsMap] = useState<Map<string, ProductRow>>(
    new Map(),
  )
  const [productsHasMore, setProductsHasMore] = useState(true)
  useEffect(() => {
    setProductsOffset(0)
    setProductsHasMore(true)
  }, [dateRangeStart, dateRangeEnd, productsSortBy])
  const productsQuery = useMemo<Query>(() => {
    const dateStart = dayjs(dateRangeStart).startOf("day").toDate()
    const today = dayjs(dateRangeEnd).endOf("day").toDate()
    return polyfillCubeQueryWithTimeDimension(
      {
        dimensions: [`${PRODUCT_PREFIX}.id`, `${PRODUCT_PREFIX}.title`],
        measures: [
          `${PRODUCT_PREFIX}.sum_first_order_revs`,
          `${PRODUCT_PREFIX}.total_revenue`,
        ],
        order: [[`${PRODUCT_PREFIX}.${productsSortBy}`, "desc"]],
        limit: TOP_PAGE_SIZE,
        offset: productsOffset,
      } as Query,
      { dateRange: [dateStart, today] },
    )
  }, [productsOffset, dateRangeStart, dateRangeEnd, productsSortBy])
  const { resultSet: productsResultSet, isLoading: isLoadingProducts } =
    useCubeQuery(productsQuery)
  const productsPage = useMemo<ProductRow[]>(() => {
    if (!productsResultSet) return []
    return productsResultSet.rawData() as ProductRow[]
  }, [productsResultSet])

  const lastProcessedProductsResultRef = useRef<unknown>(null)
  useEffect(() => {
    if (isLoadingProducts || !productsResultSet) return
    if (lastProcessedProductsResultRef.current === productsResultSet) return
    lastProcessedProductsResultRef.current = productsResultSet
    setProductsMap(prev => {
      const next =
        productsOffset === 0 ? new Map<string, ProductRow>() : new Map(prev)
      for (const row of productsPage) {
        const id = row[`${PRODUCT_PREFIX}.id`] as string | undefined
        if (id) next.set(id, row)
      }
      return next
    })
    setProductsHasMore(productsPage.length >= TOP_PAGE_SIZE)
  }, [productsResultSet, productsPage, productsOffset, isLoadingProducts])

  const loadMoreProducts = useCallback(() => {
    if (isLoadingProducts || !productsHasMore) return
    setProductsOffset(prev => prev + TOP_PAGE_SIZE)
  }, [isLoadingProducts, productsHasMore])

  // Whether the search bar is driving the view. When set, tabs/sort hide and
  // the grid shows merged results from three dedicated, one-shot search
  // queries below (separate from the paged tab queries — they have their own
  // state, no offsets, no load-more).
  const isSearching = debouncedSearch.length > 0

  // ── Universal search — three independent cube queries fan out across ads,
  // posts, and products, each `skip`ped until the user types. They share the
  // page-size limit but never paginate; the merged top results are it.
  const SEARCH_LIMIT = 30
  const searchAdsQuery = useMemo<Query>(() => {
    const { q } = MetricsMapping.brand_media_optimization_ads_table
    const dateStart = dayjs(dateRangeStart).startOf("day").toDate()
    const today = dayjs(dateRangeEnd).endOf("day").toDate()
    return polyfillCubeQueryWithTimeDimension(
      {
        ...(q as Partial<Query>),
        filters: [
          {
            dimension: `${AD_PREFIX}.ad_name`,
            operator: "contains",
            values: [debouncedSearch],
          },
        ] as Filter[],
        order: [[`${AD_PREFIX}.brand_value_agg`, "desc"]],
        limit: SEARCH_LIMIT,
      } as Query,
      { dateRange: [dateStart, today] },
    )
  }, [dateRangeStart, dateRangeEnd, debouncedSearch])
  const { resultSet: searchAdsResultSet, isLoading: isSearchingAds } =
    useCubeQuery(searchAdsQuery, { skip: !isSearching })

  const searchPostsQuery = useMemo<Query>(() => {
    const { q } = MetricsMapping.brand_media_organic_social_posts_table
    const dateStart = dayjs(dateRangeStart).startOf("day").toDate()
    const today = dayjs(dateRangeEnd).endOf("day").toDate()
    return polyfillCubeQueryWithTimeDimension(
      {
        ...(q as Partial<Query>),
        filters: [
          {
            dimension: `${POST_PREFIX}.post_caption`,
            operator: "contains",
            values: [debouncedSearch],
          },
        ] as Filter[],
        order: [[`${POST_PREFIX}.brand_value_agg`, "desc"]],
        limit: SEARCH_LIMIT,
      } as Query,
      { dateRange: [dateStart, today] },
    )
  }, [dateRangeStart, dateRangeEnd, debouncedSearch])
  const { resultSet: searchPostsResultSet, isLoading: isSearchingPosts } =
    useCubeQuery(searchPostsQuery, { skip: !isSearching })

  const searchProductsQuery = useMemo<Query>(() => {
    const dateStart = dayjs(dateRangeStart).startOf("day").toDate()
    const today = dayjs(dateRangeEnd).endOf("day").toDate()
    return polyfillCubeQueryWithTimeDimension(
      {
        dimensions: [`${PRODUCT_PREFIX}.id`, `${PRODUCT_PREFIX}.title`],
        measures: [`${PRODUCT_PREFIX}.sum_first_order_revs`],
        filters: [
          {
            dimension: `${PRODUCT_PREFIX}.title`,
            operator: "contains",
            values: [debouncedSearch],
          },
        ] as Filter[],
        order: [[`${PRODUCT_PREFIX}.sum_first_order_revs`, "desc"]],
        limit: SEARCH_LIMIT,
      } as Query,
      { dateRange: [dateStart, today] },
    )
  }, [dateRangeStart, dateRangeEnd, debouncedSearch])
  const { resultSet: searchProductsResultSet, isLoading: isSearchingProducts } =
    useCubeQuery(searchProductsQuery, { skip: !isSearching })

  const searchAdsRows = useMemo<AdRow[]>(() => {
    if (!isSearching || !searchAdsResultSet) return []
    const { postProcess } = MetricsMapping.brand_media_optimization_ads_table
    const pivot = postProcess?.(
      getPivotData(searchAdsResultSet, searchAdsQuery),
      {},
    ) as PostProcessResultSync | undefined
    return (pivot?.[0] ?? []) as AdRow[]
  }, [isSearching, searchAdsResultSet, searchAdsQuery])
  const searchPostsRows = useMemo<PostRow[]>(() => {
    if (!isSearching || !searchPostsResultSet) return []
    const { postProcess } =
      MetricsMapping.brand_media_organic_social_posts_table
    const pivot = postProcess?.(
      getPivotData(searchPostsResultSet, searchPostsQuery),
      {},
    ) as PostProcessResultSync | undefined
    return (pivot?.[0] ?? []) as PostRow[]
  }, [isSearching, searchPostsResultSet, searchPostsQuery])
  const searchProductsRows = useMemo<ProductRow[]>(() => {
    if (!isSearching || !searchProductsResultSet) return []
    return searchProductsResultSet.rawData() as ProductRow[]
  }, [isSearching, searchProductsResultSet])

  // ── Consolidated resolve: one debounced effect that fans-in every visible
  // source (paged tabs + search) and fires ONE resolveAssets request with
  // the union of not-yet-cached keys. Each source's individual effect used
  // to fire its own request in the same tick, hammering the resolve endpoint
  // and surfacing errors. Collecting keys here keeps it to one round-trip.
  useEffect(() => {
    // Wait until cube queries settle so we don't fire mid-stream.
    if (
      isLoadingAds ||
      isLoadingPosts ||
      isLoadingProducts ||
      isSearchingAds ||
      isSearchingPosts ||
      isSearchingProducts
    ) {
      return
    }
    // We deliberately do NOT pass a `url` hint with each asset — the cache
    // is populated ONLY with what resolve returns for SYNCED assets. Rows
    // whose resolve fails or isn't synced stay hidden (the converters bail
    // when their cache key is missing).
    type AssetEntry = { sourceType: AssetSourceType; externalRefId: string }
    const assets: AssetEntry[] = []
    const seen = new Set<string>()
    const consider = (
      sourceType: AssetSourceType,
      refId: string | undefined,
    ) => {
      if (!refId) return
      const key = `${sourceType}:${refId}`
      if (cachedAssetUrls[key] || seen.has(key)) return
      seen.add(key)
      assets.push({ sourceType, externalRefId: refId })
    }

    // Catalog tabs — always considered (even while searching the panels
    // stay mounted, and a user dropping search returns to them instantly).
    for (const r of topAdsPage) {
      if (!r.media_channel) continue
      consider(mapMediaChannelToSourceType(String(r.media_channel)), r.ad_id)
    }
    for (const r of topPostsPage) {
      const channel = r[`${POST_PREFIX}.media_channel`] as string | undefined
      if (!channel) continue
      consider(
        mapMediaChannelToSourceType(String(channel), true),
        r[`${POST_PREFIX}.post_id`] as string | undefined,
      )
    }
    for (const r of productsPage) {
      consider(
        "SHOPIFY_PRODUCT",
        r[`${PRODUCT_PREFIX}.id`] as string | undefined,
      )
    }
    // Search rows — only matter when the user is actively searching.
    if (isSearching) {
      for (const r of searchAdsRows) {
        if (!r.media_channel) continue
        consider(mapMediaChannelToSourceType(String(r.media_channel)), r.ad_id)
      }
      for (const r of searchPostsRows) {
        const channel = r[`${POST_PREFIX}.media_channel`] as string | undefined
        if (!channel) continue
        consider(
          mapMediaChannelToSourceType(String(channel), true),
          r[`${POST_PREFIX}.post_id`] as string | undefined,
        )
      }
      for (const r of searchProductsRows) {
        consider(
          "SHOPIFY_PRODUCT",
          r[`${PRODUCT_PREFIX}.id`] as string | undefined,
        )
      }
    }

    if (assets.length === 0) return
    // Sort the signature so insertion-order doesn't accidentally split the
    // dedup across runs.
    const sig = [...seen].sort().join(",")
    if (sig === lastResolvedSigRef.current) return
    lastResolvedSigRef.current = sig

    setIsResolving(true)
    const queryResult = dispatch(
      assetsApi.endpoints.resolveAssets.initiate({ assets }),
    )
    queryResult
      .unwrap()
      .then(result => {
        for (const [cacheKey, asset] of Object.entries(result.results)) {
          if (!asset || asset.syncStatus !== "SYNCED") continue
          const resolvedUrl = asset.url || asset.thumbnailUrl
          if (!resolvedUrl) continue
          // Cache the full asset block (including its library id) so the
          // converters can stamp `assetId` onto the ScrapedImage. AI flows
          // (extend placements, motion video, image variations) require it
          // — without `assetId` the backend can't resolve bytes server-side.
          dispatch(
            cacheAssetInfo({
              key: cacheKey,
              info: {
                url: resolvedUrl,
                thumbnailUrl: asset.thumbnailUrl,
                mediaType: asset.mediaType,
                width: asset.width,
                height: asset.height,
                assetId: asset.id,
                variantCount: asset.variantCount,
              },
            }),
          )
        }
      })
      .catch(() => {
        /* swallow — cells just stay hidden until the next attempt */
      })
      .finally(() => {
        queryResult.reset()
        setIsResolving(false)
      })
    // NOTE: `cachedAssetUrls` is INTENTIONALLY omitted from this dep array
    // even though the effect body reads it (to skip already-cached keys).
    // Including it would re-fire the effect every time the resolve response
    // populates the cache, infinitely looping. The sig-hash guard
    // (`lastResolvedSigRef`) makes the omission safe: even if a stale
    // cachedAssetUrls reference causes us to enqueue an already-cached id,
    // the dispatch is a no-op past the first call for that signature.
    // Do NOT let exhaustive-deps auto-add it.
  }, [
    topAdsPage,
    topPostsPage,
    productsPage,
    searchAdsRows,
    searchPostsRows,
    searchProductsRows,
    isSearching,
    isLoadingAds,
    isLoadingPosts,
    isLoadingProducts,
    isSearchingAds,
    isSearchingPosts,
    isSearchingProducts,
    dispatch,
  ])

  const isSearchLoading =
    isSearching && (isSearchingAds || isSearchingPosts || isSearchingProducts)

  // ── Convert each data source to the common ScrapedImage type ──
  const tabItems = useMemo<ScrapedImage[]>(() => {
    if (isSearching) {
      const ads = searchAdsRows
        .map(row => adRowToScrapedImage(row, cachedAssets))
        .filter((x): x is ScrapedImage => !!x)
      const posts = searchPostsRows
        .map(row => postRowToScrapedImage(row, POST_PREFIX, cachedAssets))
        .filter((x): x is ScrapedImage => !!x)
      const products = searchProductsRows
        .map(row => productRowToScrapedImage(row, PRODUCT_PREFIX, cachedAssets))
        .filter((x): x is ScrapedImage => !!x)
      return [...ads, ...posts, ...products]
    }
    switch (activeTab) {
      case "selected":
        return pickedAssets
      case "latest":
        return libraryItems
      case "top-ads":
        return topAds
          .map(row => adRowToScrapedImage(row, cachedAssets))
          .filter((x): x is ScrapedImage => !!x)
      case "top-posts":
        return topPosts
          .map(row => postRowToScrapedImage(row, POST_PREFIX, cachedAssets))
          .filter((x): x is ScrapedImage => !!x)
      case "top-products":
        return Array.from(productsMap.values())
          .map(row =>
            productRowToScrapedImage(row, PRODUCT_PREFIX, cachedAssets),
          )
          .filter((x): x is ScrapedImage => !!x)
      default:
        return []
    }
  }, [
    isSearching,
    searchAdsRows,
    searchPostsRows,
    searchProductsRows,
    activeTab,
    pickedAssets,
    libraryItems,
    topAds,
    topPosts,
    productsMap,
    cachedAssets,
  ])

  // Build a parallel map of asset id → formatted sort-metric value so the
  // drawer cards can render a "$1.2K" / "230 shares" style badge. Currency
  // metrics get a "$" prefix via formatNumber; engagement counts (shares,
  // saves) are plain compact numbers. Posts rows keep the cube prefix on
  // their keys; ads rows have it stripped by their postProcess.
  const metricValueByAssetId = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>()
    const isCurrency = (metric: string): boolean => {
      if (metric === "shares" || metric === "saves") return false
      // brand_value_agg, purchase_value, sum_first_order_revs, total_revenue
      return true
    }
    const fmt = (n: number, metric: string) =>
      formatNumber(n, { compact: true, currency: isCurrency(metric) })
    const fromAdRow = (row: AdRow, key: string) => {
      const raw = (row as unknown as Record<string, unknown>)[key]
      const n = typeof raw === "string" ? Number(raw) : (raw as number)
      if (Number.isFinite(n) && row.ad_id) {
        map.set(`ad-${row.ad_id}`, fmt(n, key))
      }
    }
    const fromPostRow = (row: PostRow, key: string) => {
      const raw = row[`${POST_PREFIX}.${key}`]
      const n = typeof raw === "string" ? Number(raw) : (raw as number)
      const postId = row[`${POST_PREFIX}.post_id`] as string | undefined
      if (Number.isFinite(n) && postId) {
        map.set(`post-${postId}`, fmt(n, key))
      }
    }
    const fromProductRow = (row: ProductRow, key: string) => {
      const raw = row[`${PRODUCT_PREFIX}.${key}`]
      const n = typeof raw === "string" ? Number(raw) : (raw as number)
      const id = row[`${PRODUCT_PREFIX}.id`] as string | undefined
      if (Number.isFinite(n) && id) {
        map.set(`product-${id}`, fmt(n, key))
      }
    }

    if (isSearching) {
      // Search queries use a fixed sort per source — see *Query useMemos.
      for (const r of searchAdsRows) fromAdRow(r, "brand_value_agg")
      for (const r of searchPostsRows) fromPostRow(r, "brand_value_agg")
      for (const r of searchProductsRows) {
        fromProductRow(r, "sum_first_order_revs")
      }
      return map
    }
    if (activeTab === "top-ads") {
      for (const r of topAds) fromAdRow(r, adsSortBy)
    } else if (activeTab === "top-posts") {
      for (const r of topPosts) fromPostRow(r, postsSortBy)
    } else if (activeTab === "top-products") {
      for (const r of productsMap.values()) fromProductRow(r, productsSortBy)
    }
    return map
  }, [
    isSearching,
    activeTab,
    adsSortBy,
    postsSortBy,
    productsSortBy,
    topAds,
    topPosts,
    productsMap,
    searchAdsRows,
    searchPostsRows,
    searchProductsRows,
  ])

  // ── Drop assets whose thumbnails failed to load. The client-side text
  // filter only runs in non-search mode — when searching, cube already
  // narrowed each source by `contains` so re-filtering on sourceUrl/id
  // (which doesn't carry the actual ad/post/product name) would wrongly
  // hide matches. ──
  const visibleItems = useMemo<ScrapedImage[]>(() => {
    let items = tabItems
    if (failedAssetIds.size > 0) {
      items = items.filter(img => !failedAssetIds.has(img.id))
    }
    if (isSearching || !debouncedSearch) return items
    const q = debouncedSearch.toLowerCase()
    return items.filter(img => {
      const hay = `${img.sourceUrl ?? ""} ${img.assetId ?? ""} ${img.id}`
      return hay.toLowerCase().includes(q)
    })
  }, [tabItems, debouncedSearch, failedAssetIds, isSearching])

  // ── Product-feed tags for every visible library asset, in ONE request.
  // Sorted + deduped so the cache key is stable across re-renders, capped at
  // the endpoint's 200-id limit. Synthetic pool ids (blob fallbacks,
  // mega-feed seeds) have no library id and simply aren't asked about.
  const visibleAssetIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of visibleItems) {
      if (item.assetId) ids.add(item.assetId)
    }
    return [...ids].sort().slice(0, 200)
  }, [visibleItems])
  const { data: bulkProductTags } = useBulkProductTagsQuery(
    { assetIds: visibleAssetIds },
    { skip: visibleAssetIds.length === 0 },
  )
  const productTagsByAssetId = bulkProductTags?.results

  // Full-screen loader: shown when the active view has nothing to render and
  // its underlying fetch (cube OR asset resolve) is still in flight. Cards
  // intentionally wait for resolved URLs (see converters), so we treat the
  // resolve window as loading too.
  // True while the active tab is actively waiting for a fetch or resolve to
  // complete. Previously also returned true while underlying rows existed
  // (covering the resolve window after cube returned) — but when every row's
  // asset resolved to FAILED, the cache never populated and the spinner
  // would spin forever. Only `isLoadingX` / `isResolving` are real signals;
  // the post-resolve "nothing rendered" case is handled as an error below.
  const showFullLoader = (() => {
    if (visibleItems.length > 0) return false
    if (isSearching) return isSearchLoading || isResolving
    if (activeTab === "top-ads") return isLoadingAds || isResolving
    if (activeTab === "top-posts") return isLoadingPosts || isResolving
    if (activeTab === "top-products") return isLoadingProducts || isResolving
    if (isLibraryTab) return isLoadingLibrary
    return false
  })()

  // True when the active source produced rows but none could be turned into
  // a rendered tile (e.g. every Shopify product's resolveAssets entry came
  // back with status=FAILED, or every cube row's resolved asset failed to
  // load its thumbnail). Used to swap the indefinite spinner for an error.
  const hasUnderlyingRows = (() => {
    if (isSearching) {
      return (
        searchAdsRows.length > 0 ||
        searchPostsRows.length > 0 ||
        searchProductsRows.length > 0
      )
    }
    if (activeTab === "top-ads") return topAds.length > 0
    if (activeTab === "top-posts") return topPosts.length > 0
    if (activeTab === "top-products") return productsMap.size > 0
    if (isLibraryTab) return libraryMap.size > 0
    return false
  })()

  // Per-tab Load More button state. Search has no pagination — it shows the
  // top N matches and stops there.
  const canLoadMore = (() => {
    if (isSearching) return false
    if (activeTab === "top-ads") return topAdsHasMore
    if (activeTab === "top-posts") return topPostsHasMore
    if (activeTab === "top-products") return productsHasMore
    // Hide the Load More button until the first page has actually landed —
    // otherwise the default `libraryHasMore=true` makes it appear before any
    // request fires (and during the in-flight fetch it'd render as "Loading…"
    // even though the top-of-grid spinner is already saying that).
    if (isLibraryTab) {
      return libraryHasMore && libraryMap.size > 0 && !isLoadingLibrary
    }
    return false
  })()
  const isLoadingMore = (() => {
    if (isSearching) return false
    // Keep the button spinning through the resolve window after cube returns
    // — otherwise it would briefly say "Load more" while cards are still
    // popping in.
    if (activeTab === "top-ads") return isLoadingAds || isResolving
    if (activeTab === "top-posts") return isLoadingPosts || isResolving
    if (activeTab === "top-products") return isLoadingProducts || isResolving
    if (isLibraryTab) return isLoadingLibrary
    return false
  })()
  const handleLoadMore = useCallback(() => {
    if (activeTab === "top-ads") loadMoreTopAds()
    else if (activeTab === "top-posts") loadMoreTopPosts()
    else if (activeTab === "top-products") loadMoreProducts()
    else if (isLibraryTab) loadMoreLibrary()
  }, [
    activeTab,
    isLibraryTab,
    loadMoreTopAds,
    loadMoreTopPosts,
    loadMoreProducts,
    loadMoreLibrary,
  ])

  return (
    <>
      {/* Inline assets panel — a permanent, full-height right column on the
          AdForge page (no longer a floating drawer). Drag a tile onto a card
          slot on the left; drag a slot's asset back here to clear it. */}
      <aside className="w-[360px] flex-shrink-0 h-full bg-system-gray-900/95 border-l border-system-gray-700/50 flex flex-col">
        {/* The motion container staggers each section (header, search, tabs,
              filters, actions, items) so the panel feels like it fills in on
              mount. `display: contents` keeps the wrapper invisible to flex. */}
        <motion.div
          variants={drawerSectionsVariants}
          initial="hidden"
          animate="visible"
          style={{ display: "contents" }}
        >
          {/* Header */}
          <motion.div
            variants={drawerSectionVariants}
            className="flex items-center justify-between px-4 py-3 border-b border-system-gray-800/50"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">Assets</p>
              <p className="text-[10px] text-system-gray-500 mt-0.5">
                {pickedAssets.length} staged · drag into a card
              </p>
            </div>
          </motion.div>

          {/* Search — commits only on blur, Enter, or the explicit search
            button, so typing doesn't fan out cube requests on every
            keystroke. While searching, the leading icon becomes a back arrow
            that clears + returns to tabs. */}
          <motion.div
            variants={drawerSectionVariants}
            className="px-4 pt-3 pb-2 border-b border-system-gray-800/50"
          >
            <div className="relative">
              {isSearching ? (
                <button
                  onClick={clearSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-brand hover:text-white transition"
                  title="Back to tabs"
                  aria-label="Back to tabs"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={commitSearch}
                  disabled={!search.trim()}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-system-gray-500 hover:text-brand transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Search"
                  aria-label="Search"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              )}
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onBlur={commitSearch}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    commitSearch()
                  } else if (e.key === "Escape") {
                    clearSearch()
                  }
                }}
                placeholder="Search ads, posts, products…"
                className="input w-full h-10 pl-10 pr-10 bg-system-gray-800 border-system-gray-700 text-white placeholder-system-gray-500 focus:border-brand focus:outline-none text-sm"
              />
              {search && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-system-gray-500 hover:text-white"
                  title="Clear search"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>

          {/* Tabs — hidden while searching: the search bar fans out across all
            three catalog sources, so a per-tab grouping would be misleading. */}
          {!isSearching && (
            <motion.div
              variants={drawerSectionVariants}
              className="px-2 pt-2 border-b border-system-gray-800/50 flex gap-1 overflow-hidden"
            >
              {TABS.map(t => {
                const isActive = t.key === activeTab
                // Tiny count next to the Selected tab so users see staged total
                // without switching tabs.
                const badge =
                  t.key === "selected" ? pickedAssets.length : undefined
                return (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={cn(
                      "flex-shrink-0 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition",
                      isActive
                        ? "bg-brand/15 text-brand"
                        : "text-system-gray-400 hover:text-white hover:bg-system-gray-800",
                    )}
                  >
                    {t.label}
                    {typeof badge === "number" && badge > 0 && (
                      <span className="ml-1 text-system-gray-500">
                        ({badge})
                      </span>
                    )}
                  </button>
                )
              })}
            </motion.div>
          )}

          {/* Latest-tab source filter — All vs. only user uploads. Lives as a
            sub-row because a sixth tab overflows the strip. */}
          {!isSearching && isLibraryTab && (
            <motion.div
              variants={drawerSectionVariants}
              className="px-4 py-1.5 border-b border-system-gray-800/50 flex items-center gap-1"
            >
              <span className="text-[9px] uppercase tracking-wide text-system-gray-500 mr-1">
                Source
              </span>
              {LIBRARY_SOURCES.map(s => (
                <button
                  key={s.key}
                  onClick={() => setLibrarySource(s.key)}
                  className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-semibold transition",
                    librarySource === s.key
                      ? "bg-brand/15 text-brand"
                      : "text-system-gray-400 hover:text-white hover:bg-system-gray-800",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </motion.div>
          )}

          {/* Filters row — date range whenever we're showing catalog results
            (either a catalog tab or the universal search). The per-tab sort
            selector is hidden while searching because results come from all
            three sources at once. Selected is a local pool, no filters. */}
          {(isSearching || (activeTab !== "selected" && !isLibraryTab)) && (
            <motion.div
              variants={drawerSectionVariants}
              className="px-4 py-2 border-b border-system-gray-800/50 flex flex-col gap-2"
            >
              <label className="flex flex-col w-full">
                <span className="text-[9px] uppercase tracking-wide text-system-gray-500 mb-0.5">
                  Date range
                </span>
                <DateRangePicker
                  start={dateRangeStart}
                  end={dateRangeEnd}
                  comparisonEnabled={false}
                  onChange={({ start, end }) => {
                    setDateRangeStart(start)
                    setDateRangeEnd(end)
                  }}
                />
              </label>
              {!isSearching && (
                <label className="flex flex-col w-full">
                  <span className="text-[9px] uppercase tracking-wide text-system-gray-500 mb-0.5">
                    Sort by
                  </span>
                  {activeTab === "top-ads" && (
                    <select
                      value={adsSortBy}
                      onChange={e =>
                        setAdsSortBy(
                          e.target.value as (typeof AD_SORTS)[number]["value"],
                        )
                      }
                      className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none [&>option]:bg-system-gray-800 [&>option]:text-white p-0 pl-2"
                    >
                      {AD_SORTS.map(s => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {activeTab === "top-posts" && (
                    <select
                      value={postsSortBy}
                      onChange={e =>
                        setPostsSortBy(
                          e.target
                            .value as (typeof POST_SORTS)[number]["value"],
                        )
                      }
                      className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none [&>option]:bg-system-gray-800 [&>option]:text-white p-0 pl-2"
                    >
                      {POST_SORTS.map(s => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {activeTab === "top-products" && (
                    <select
                      value={productsSortBy}
                      onChange={e =>
                        setProductsSortBy(
                          e.target
                            .value as (typeof PRODUCT_SORTS)[number]["value"],
                        )
                      }
                      className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none [&>option]:bg-system-gray-800 [&>option]:text-white p-0 pl-2"
                    >
                      {PRODUCT_SORTS.map(s => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              )}
            </motion.div>
          )}

          {/* Upload action — only when on Selected tab and not searching.
            "Add" used to open a separate asset-picker modal; now the search
            and catalog tabs above ARE the picker. */}
          {activeTab === "selected" && !isSearching && (
            <motion.div
              variants={drawerSectionVariants}
              className="flex items-center gap-2 px-4 py-2 border-b border-system-gray-800/50 overflow-hidden"
            >
              <button
                onClick={onUploadClick}
                disabled={isUploading}
                className="btn btn-xs btn-ghost text-brand gap-1 flex-1 min-w-0"
              >
                {isUploading ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <ArrowUpTrayIcon className="w-3 h-3" />
                )}
                Upload
              </button>
              <button
                onClick={onScrapeClick}
                className="btn btn-xs btn-ghost text-brand gap-1 flex-1 min-w-0"
              >
                <GlobeAltIcon className="w-3 h-3" />
                Scrape URL
              </button>
            </motion.div>
          )}

          {/* Content area — also acts as a drop target so dragging a slot's
            asset back here removes it from the ad. */}
          <motion.div
            ref={setNodeRef}
            variants={drawerSectionVariants}
            className={cn(
              "flex-1 overflow-y-auto px-4 py-3 transition-colors",
              isOver && "bg-brand/5",
            )}
          >
            {(() => {
              // Two failure shapes:
              //  1) tabItems exist but every thumbnail 404'd (drop to
              //     `failedAssetIds`), so visibleItems is empty.
              //  2) underlying source returned rows, but every row's asset
              //     resolution failed (status=FAILED) so the converters
              //     dropped them and tabItems never populated.
              // Either way: not loading anything, but nothing to render.
              const allItemsFailed =
                !showFullLoader &&
                visibleItems.length === 0 &&
                (tabItems.length > 0 || hasUnderlyingRows)
              const loadMoreButton = canLoadMore ? (
                <div className="flex items-center justify-center pt-3 mt-1">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="btn btn-xs btn-ghost text-brand gap-1"
                  >
                    {isLoadingMore ? (
                      <>
                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                        Loading…
                      </>
                    ) : (
                      "Load more"
                    )}
                  </button>
                </div>
              ) : null
              if (allItemsFailed) {
                return (
                  <>
                    <TabEmpty
                      icon={ExclamationTriangleIcon}
                      message="Couldn't load any assets"
                      hint="The thumbnails returned errors. Try another tab or load more below."
                    />
                    {loadMoreButton}
                  </>
                )
              }
              if (showFullLoader) return <TabSpinner label="Loading…" />

              if (visibleItems.length === 0) {
                if (debouncedSearch) {
                  return (
                    <TabEmpty
                      icon={MagnifyingGlassIcon}
                      message={`No matches for "${debouncedSearch}"`}
                    />
                  )
                }
                if (activeTab === "selected") {
                  return (
                    <TabEmpty
                      icon={PhotoIcon}
                      message="No assets staged yet"
                      hint="Click Add or Upload above, or drag from another tab."
                    />
                  )
                }
                return <TabEmpty icon={PhotoIcon} message="Nothing to show" />
              }

              return (
                <>
                  {/* Grid uses motion variants so when the drawer expands
                        from the minimal rail to the full panel, the items
                        cascade in (fade + tiny scale-up) instead of all
                        snapping into view at once. `key={activeTab}` resets
                        the cascade when the user switches tabs. */}
                  <motion.div
                    key={activeTab}
                    variants={itemsGridVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-2 gap-2"
                  >
                    {visibleItems.map(asset => (
                      <motion.div
                        key={`${activeTab}-${asset.id}`}
                        variants={itemTileVariants}
                      >
                        <DrawerItem
                          asset={asset}
                          tab={activeTab}
                          slotIndices={slotsByAssetId.get(asset.id) ?? []}
                          placementLabels={placementLabelsByAssetId?.get(
                            asset.id,
                          )}
                          isDemoEnabled={isDemoEnabled}
                          isPicked={
                            (isSearching || activeTab !== "selected") &&
                            pickedAssetIds.has(asset.id)
                          }
                          // Sort-metric value — only meaningful on catalog
                          // tabs / search. The Selected tab has no metric.
                          metricValue={
                            activeTab === "selected" && !isSearching
                              ? undefined
                              : metricValueByAssetId.get(asset.id)
                          }
                          // X-button only makes sense for the Selected tab — other
                          // tabs (and search, which always shows catalog items)
                          // are read-only catalogs.
                          onRemoveFromPool={
                            !isSearching && activeTab === "selected"
                              ? onRemoveFromPool
                              : undefined
                          }
                          // Tap toggles the catalog item in/out of the pool.
                          // Stay on the current view so users can keep selecting.
                          onTap={
                            !isSearching && activeTab === "selected"
                              ? undefined
                              : tappedAsset => {
                                  if (pickedAssetIds.has(tappedAsset.id)) {
                                    onRemoveFromPool(tappedAsset.id)
                                  } else {
                                    onAddToPool(tappedAsset)
                                  }
                                }
                          }
                          onPickThumbnail={onPickThumbnail}
                          onDownload={asset => void handleDownload(asset)}
                          isDownloading={downloadingId === asset.id}
                          onImageError={markAssetFailed}
                          productTags={
                            asset.assetId
                              ? productTagsByAssetId?.[asset.assetId]
                              : undefined
                          }
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                  {/* Explicit Load more button for catalog tabs. Hidden during
                    search (search shows top N matches with no pagination)
                    and on Selected (it's a local pool). */}
                  {loadMoreButton}
                </>
              )
            })()}
          </motion.div>
        </motion.div>
      </aside>
    </>
  )
}
